/**
 * ProPlusSubscriptionsTab — Site Admin > Rivalry Ops > Pro+ Subscriptions
 *
 * iter87.76 — Replaces the Mobile Access panel. Two collapsible sub-panels
 * mirroring the compliance-ops UI DNA:
 *   ▼ Pro+ Requests  — approve / reject
 *   ▼ Pro+ Activity  — suspend / reinstate / close / archive / delete
 *
 * Scope reference: /app/memory/PRO_PLUS_V1_SCOPE.md §8.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Star, ChevronDown, ChevronRight, Loader2, Search, Play, Ban,
    Pause, RotateCcw, Archive, XCircle, Trash2, User, ShieldAlert, Mail,
} from "lucide-react";
import ProPlusBillingSection from "./ProPlusBillingSection";

const REQUEST_STATUS_COLOR = {
    pending: "text-amber-400 border-amber-400/40",
    approved: "text-emerald-400 border-emerald-400/40",
    rejected: "text-red-400 border-red-400/40",
};
const SUB_STATUS_COLOR = {
    live: "text-emerald-400 border-emerald-400/40",
    suspended: "text-orange-400 border-orange-400/40",
    closed: "text-zinc-400 border-[#333]",
    archived: "text-zinc-500 border-[#333]",
};

const PANEL_KEY_REQ = "sa.proplus.panels.requests";
const PANEL_KEY_ACT = "sa.proplus.panels.activity";
const PANEL_KEY_TST = "sa.proplus.panels.testers";
// iter87.199.101 — Sub-tab persistence for the two-lane Status / Tools split.
const SUBTAB_KEY = "sa.proplus.subtab";

function loadPanelState(key, def) {
    try {
        const v = localStorage.getItem(key);
        return v === null ? def : v === "1";
    } catch {
        return def;
    }
}
function savePanelState(key, v) {
    try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* noop */ }
}
function loadSubtab() {
    try {
        const v = localStorage.getItem(SUBTAB_KEY);
        return v === "tools" ? "tools" : "status";
    } catch {
        return "status";
    }
}
function saveSubtab(v) {
    try { localStorage.setItem(SUBTAB_KEY, v); } catch { /* noop */ }
}

export default function ProPlusSubscriptionsTab() {
    const [reqsOpen, setReqsOpen] = useState(() => loadPanelState(PANEL_KEY_REQ, true));
    const [actsOpen, setActsOpen] = useState(() => loadPanelState(PANEL_KEY_ACT, true));
    const [subtab, setSubtab] = useState(() => loadSubtab()); // iter87.199.101 — "status" | "tools"
    const [requests, setRequests] = useState([]);
    const [subs, setSubs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [reqFilter, setReqFilter] = useState("pending");
    const [subFilter, setSubFilter] = useState("live");
    const [reqQuery, setReqQuery] = useState("");
    const [subQuery, setSubQuery] = useState("");
    // iter87.199.86 — Break-glass toggle for legacy sign-in on /rivalry-pro/login.
    const [legacyEnabled, setLegacyEnabled] = useState(false);
    const [legacyBusy, setLegacyBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [r, s, settings] = await Promise.all([
                api.proPlusListRequests({ status: reqFilter, q: reqQuery || undefined }),
                api.proPlusListSubs({ status: subFilter, q: subQuery || undefined }),
                api.getSiteSettings(),
            ]);
            setRequests(r.items || []);
            setSubs(s.items || []);
            setLegacyEnabled(Boolean(settings?.pro_plus_legacy_signin_enabled));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load Pro+ data");
        }
        setBusy(false);
    }, [reqFilter, reqQuery, subFilter, subQuery]);

    useEffect(() => { load(); }, [load]);

    const togglePanel = (which) => {
        if (which === "req") {
            const next = !reqsOpen;
            setReqsOpen(next);
            savePanelState(PANEL_KEY_REQ, next);
        } else {
            const next = !actsOpen;
            setActsOpen(next);
            savePanelState(PANEL_KEY_ACT, next);
        }
    };

    const pendingCount = requests.filter((r) => r.status === "pending").length;
    const liveCount = subs.filter((s) => s.status === "live").length;
    const suspendedCount = subs.filter((s) => s.status === "suspended").length;

    const toggleLegacy = async () => {
        const next = !legacyEnabled;
        setLegacyBusy(true);
        try {
            const updated = await api.updateSiteSettings({
                pro_plus_legacy_signin_enabled: next,
            });
            setLegacyEnabled(Boolean(updated?.pro_plus_legacy_signin_enabled));
            toast.success(
                next
                    ? "Legacy sign-in link is now visible on /rivalry-pro/login"
                    : "Legacy sign-in link is now hidden",
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update legacy toggle");
        }
        setLegacyBusy(false);
    };

    // iter87.199.101 — Sub-tab switcher between Status (Requests + Activity)
    // and Tools (Billing / Grandfathered / LS Events / Legacy sign-in).
    const pickSubtab = (v) => { setSubtab(v); saveSubtab(v); };

    return (
        <section className="space-y-4" data-testid="pro-plus-subscriptions">
            <header className="flex items-center gap-3">
                <Star className="w-5 h-5 text-cyber" />
                <h1 className="font-display font-black text-2xl tracking-tight text-white">
                    Pro+ Subscriptions
                </h1>
                <button
                    type="button"
                    onClick={load}
                    disabled={busy}
                    data-testid="pro-plus-refresh"
                    className="brutal-btn-ghost text-xs py-1 ml-auto"
                >
                    {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                    Refresh
                </button>
            </header>

            {/* iter87.199.101 — Two-lane sub-tab: Status vs. Tools. Status
                shows the live operational queues (Requests, Activity).
                Tools houses the admin controls that don't need to be
                visible on every page load (Grant Comp, Grandfathered,
                LS Events, Legacy sign-in break-glass). */}
            <div
                className="flex gap-2"
                data-testid="pro-plus-subtabs"
                role="tablist"
            >
                <button
                    type="button"
                    role="tab"
                    aria-selected={subtab === "status"}
                    onClick={() => pickSubtab("status")}
                    data-testid="pro-plus-subtab-status"
                    className={`pill text-xs font-black uppercase tracking-wider py-1.5 px-4 transition-colors ${
                        subtab === "status"
                            ? "text-cyber border-cyber/60 bg-cyber/10"
                            : "text-zinc-400 border-[#333] hover:text-white"
                    }`}
                >
                    Status
                    {pendingCount > 0 && (
                        <span
                            className="ml-2 text-[9px] font-bold text-amber-400"
                            data-testid="pro-plus-subtab-status-badge"
                        >
                            · {pendingCount}
                        </span>
                    )}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={subtab === "tools"}
                    onClick={() => pickSubtab("tools")}
                    data-testid="pro-plus-subtab-tools"
                    className={`pill text-xs font-black uppercase tracking-wider py-1.5 px-4 transition-colors ${
                        subtab === "tools"
                            ? "text-cyber border-cyber/60 bg-cyber/10"
                            : "text-zinc-400 border-[#333] hover:text-white"
                    }`}
                >
                    Tools
                </button>
            </div>

            {subtab === "tools" && (
                <div className="space-y-4" data-testid="pro-plus-subtab-tools-panel">
                    {/* iter87.199.96 → iter87.199.101 — Billing sub-section:
                        Grant Comp Pro+, Grandfathered users, and Paddle
                        webhook events. Now scoped to the Tools tab
                        so daily-driver Status stays uncluttered. */}
                    <ProPlusBillingSection />

                    {/* iter87.199.86 — Break-glass toggle for legacy email+password
                        sign-in on /rivalry-pro/login. Default OFF now that Clerk is
                        live. Flip ON if Clerk is unreachable; rescues Pro+ users who
                        already have a `pro_plus_users` row with a password. */}
                    <div
                        className="brutal-card border-orange-500/40 p-3 flex items-start gap-3"
                        data-testid="pro-plus-legacy-signin-panel"
                    >
                        <ShieldAlert
                            className={`w-4 h-4 shrink-0 mt-0.5 ${legacyEnabled ? "text-orange-400" : "text-zinc-500"}`}
                        />
                        <div className="flex-1 min-w-0">
                            <p className="font-display font-extrabold text-xs uppercase tracking-wider text-white">
                                Enable legacy sign-in link
                            </p>
                            <p className="text-[10px] text-zinc-400 mt-1 leading-snug">
                                Break-glass only. When ON, the "Use password instead (legacy)"
                                link appears on <span className="font-mono text-zinc-300">/rivalry-pro/login</span>.
                                Flip ON only if Clerk is down; rescues Pro+ users who
                                already have a legacy password.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={toggleLegacy}
                            disabled={legacyBusy}
                            data-testid="pro-plus-legacy-signin-toggle"
                            aria-pressed={legacyEnabled}
                            className={`pill text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-40 ${
                                legacyEnabled
                                    ? "text-orange-400 border-orange-400/60 bg-orange-500/10"
                                    : "text-zinc-400 border-[#333] hover:text-white"
                            }`}
                        >
                            {legacyBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : legacyEnabled ? "ON" : "OFF"}
                        </button>
                    </div>
                </div>
            )}

            {subtab === "status" && (
                <div className="space-y-4" data-testid="pro-plus-subtab-status-panel">
                    {/* Panel 1 — Requests */}
                    <div className="brutal-card border-cyber/40" data-testid="pro-plus-requests-panel">
                        <button
                            type="button"
                            onClick={() => togglePanel("req")}
                            data-testid="pro-plus-requests-toggle"
                            className="w-full flex items-center gap-2 p-3 hover:bg-cyber/5 transition-colors"
                        >
                            {reqsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            <span className="font-display font-extrabold text-sm uppercase tracking-wider">
                                Pro+ Requests
                            </span>
                            {pendingCount > 0 && (
                                <span
                                    className="pill text-[10px] font-bold uppercase text-amber-400 border-amber-400/40 ml-2"
                                    data-testid="pro-plus-pending-badge"
                                >
                                    {pendingCount} pending
                                </span>
                            )}
                        </button>
                        {reqsOpen && (
                            <div className="p-3 border-t border-[#222] space-y-3">
                                <FilterBar
                                    query={reqQuery}
                                    setQuery={setReqQuery}
                                    filter={reqFilter}
                                    setFilter={setReqFilter}
                                    options={[
                                        { k: "pending", label: "Pending" },
                                        { k: "rejected", label: "Rejected" },
                                        { k: "approved", label: "Approved" },
                                        { k: "all", label: "All" },
                                    ]}
                                    testIdPrefix="pro-plus-request"
                                />
                                {requests.length === 0 ? (
                                    <p className="text-xs text-zinc-500 italic p-4 text-center">
                                        No requests match this filter.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {requests.map((r) => (
                                            <RequestRow key={r.id} row={r} onChanged={load} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Panel 2 — Activity */}
                    <div className="brutal-card border-cyber/40" data-testid="pro-plus-activity-panel">
                        <button
                            type="button"
                            onClick={() => togglePanel("act")}
                            data-testid="pro-plus-activity-toggle"
                            className="w-full flex items-center gap-2 p-3 hover:bg-cyber/5 transition-colors"
                        >
                            {actsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            <span className="font-display font-extrabold text-sm uppercase tracking-wider">
                                Pro+ Activity
                            </span>
                            <span className="text-[10px] text-zinc-500 ml-2">
                                {liveCount} live · {suspendedCount} suspended
                            </span>
                        </button>
                        {actsOpen && (
                            <div className="p-3 border-t border-[#222] space-y-3">
                                <FilterBar
                                    query={subQuery}
                                    setQuery={setSubQuery}
                                    filter={subFilter}
                                    setFilter={setSubFilter}
                                    options={[
                                        { k: "live", label: "Live" },
                                        { k: "suspended", label: "Suspended" },
                                        { k: "closed", label: "Closed" },
                                        { k: "archived", label: "Archived" },
                                        { k: "all", label: "All" },
                                    ]}
                                    testIdPrefix="pro-plus-sub"
                                />
                                {subs.length === 0 ? (
                                    <p className="text-xs text-zinc-500 italic p-4 text-center">
                                        No subscriptions match this filter.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {subs.map((s) => (
                                            <SubscriptionRow key={s.id} sub={s} onChanged={load} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* iter87.76.1 — Testers panel removed. Mobile Testers are
                managed at Platform level (Mezzanine → Mobile Testers)
                because they are a "competing claim space" against Pro+
                subscribers — a league claimed by a tester cannot also
                be claimed by a Pro+ subscriber and vice versa.
                Kept the third panel_key in localStorage semantics for
                a possible future re-add if the mutual-exclusion policy
                changes; the button + list are simply not rendered. */}
        </section>
    );
}

function FilterBar({ query, setQuery, filter, setFilter, options, testIdPrefix }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                    data-testid={`${testIdPrefix}-search`}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search email…"
                    className="brutal-input text-xs w-full pl-7"
                />
            </div>
            <div className="flex gap-1" data-testid={`${testIdPrefix}-filter-tabs`}>
                {options.map((o) => (
                    <button
                        key={o.k}
                        type="button"
                        onClick={() => setFilter(o.k)}
                        data-testid={`${testIdPrefix}-filter-${o.k}`}
                        className={`pill text-[10px] uppercase transition-colors ${
                            filter === o.k
                                ? "text-cyber border-cyber/60 bg-cyber/10"
                                : "text-zinc-400 border-[#333] hover:text-white"
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function RequestRow({ row, onChanged }) {
    const [expanded, setExpanded] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const approve = async () => {
        setBusy(true);
        try {
            await api.proPlusApprove(row.id);
            toast.success(`Approved — ${row.email} is now Pro+`);
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Approve failed");
        }
        setBusy(false);
    };

    const reject = async () => {
        if (reason.trim().length < 3) {
            toast.error("Rejection reason must be at least 3 characters");
            return;
        }
        setBusy(true);
        try {
            await api.proPlusReject(row.id, reason.trim());
            toast.success("Request rejected");
            setRejectOpen(false);
            setReason("");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reject failed");
        }
        setBusy(false);
    };

    return (
        <div className="brutal-card-light p-3 space-y-2" data-testid={`pro-plus-request-${row.id}`}>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex flex-wrap items-center gap-2 text-left"
                data-testid={`pro-plus-request-toggle-${row.id}`}
            >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <span className={`pill text-[10px] font-bold uppercase ${REQUEST_STATUS_COLOR[row.status] || "text-zinc-400"}`}>
                    {row.status}
                </span>
                <span className="font-mono text-xs text-white">{row.email}</span>
                {row.source === "clerk_sign_up" && (
                    <span
                        className="pill text-[9px] font-bold uppercase border-cyber/40 text-cyber"
                        data-testid={`pro-plus-request-source-clerk-${row.id}`}
                        title="Auto-created from Clerk sign-up"
                    >
                        Clerk
                    </span>
                )}
                <span className="text-[10px] text-zinc-500 ml-auto">
                    {(row.submitted_at || "").slice(0, 16).replace("T", " ")}
                </span>
            </button>
            {expanded && (
                <div className="border-t border-[#222] pt-2 space-y-2">
                    {row.notes && (
                        <p className="text-[11px] text-zinc-300">
                            <User className="w-3 h-3 inline mr-1" />
                            {row.notes}
                        </p>
                    )}
                    {row.rejection_reason && (
                        <p className="text-[11px] text-red-400" data-testid={`pro-plus-rejection-reason-${row.id}`}>
                            Rejected: {row.rejection_reason}
                        </p>
                    )}
                    {row.status === "pending" && (
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={approve}
                                disabled={busy}
                                data-testid={`pro-plus-approve-${row.id}`}
                                className="brutal-btn-primary text-xs py-1.5"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                                Approve
                            </button>
                            <button
                                type="button"
                                onClick={() => setRejectOpen((v) => !v)}
                                data-testid={`pro-plus-reject-toggle-${row.id}`}
                                className="brutal-btn-ghost text-xs py-1.5 text-red-400"
                            >
                                <Ban className="w-3 h-3" /> Reject…
                            </button>
                        </div>
                    )}
                    {rejectOpen && row.status === "pending" && (
                        <div className="flex gap-2 items-stretch" data-testid={`pro-plus-reject-row-${row.id}`}>
                            <input
                                data-testid={`pro-plus-reject-reason-${row.id}`}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Rejection reason (min 3 chars)"
                                className="brutal-input text-xs flex-1"
                                maxLength={1000}
                            />
                            <button
                                type="button"
                                onClick={reject}
                                disabled={busy || reason.trim().length < 3}
                                data-testid={`pro-plus-reject-submit-${row.id}`}
                                className="brutal-btn-ghost text-xs py-2 text-red-400 disabled:opacity-40"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirm reject"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function SubscriptionRow({ sub, onChanged }) {
    const [expanded, setExpanded] = useState(false);
    const [suspendOpen, setSuspendOpen] = useState(false);
    const [suspendReason, setSuspendReason] = useState("past_due");
    const [suspendNote, setSuspendNote] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [showDelete, setShowDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    const runAction = async (fn, msg) => {
        setBusy(true);
        try {
            await fn();
            toast.success(msg);
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || `${msg} failed`);
        }
        setBusy(false);
    };

    return (
        <div className="brutal-card-light p-3 space-y-2" data-testid={`pro-plus-sub-${sub.id}`}>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex flex-wrap items-center gap-2 text-left"
                data-testid={`pro-plus-sub-toggle-${sub.id}`}
            >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <span className={`pill text-[10px] font-bold uppercase ${SUB_STATUS_COLOR[sub.status] || "text-zinc-400"}`}>
                    {sub.status}
                </span>
                <span className="font-mono text-[10px] text-cyber">{sub.id}</span>
                <span className="font-mono text-xs text-white">{sub.email}</span>
                <span className="pill text-[10px] uppercase border-[#333] text-zinc-400">
                    {sub.leagues_count ?? 0} leagues
                </span>
                {sub.status === "suspended" && (
                    <span className="text-[10px] text-orange-400 ml-auto">
                        {sub.suspended_reason}
                    </span>
                )}
                {/* Paddle scheduled a cancellation but access continues
                    until the period ends — status stays "live" by design,
                    so this is the ONLY visual signal an admin gets that
                    it's not actually renewing. Previously missing entirely. */}
                {sub.cancel_pending && sub.status === "live" && (
                    <span
                        className="pill text-[10px] font-bold uppercase border-amber-400/50 text-amber-400 bg-amber-400/10 ml-auto"
                        data-testid={`pro-plus-sub-cancel-pending-${sub.id}`}
                    >
                        Cancelling{sub.access_until ? ` · ends ${sub.access_until.slice(0, 10)}` : ""}
                    </span>
                )}
                {sub.renew_at && sub.status === "live" && !sub.cancel_pending && (
                    <span className="text-[10px] text-zinc-500 ml-auto">
                        Renews {sub.renew_at.slice(0, 10)}
                    </span>
                )}
            </button>
            {expanded && (
                <div className="border-t border-[#222] pt-2 space-y-2">
                    <p className="text-[11px] text-zinc-400">
                        <User className="w-3 h-3 inline mr-1" />
                        {sub.email} · Source: <strong>{sub.source}</strong> · Live since {(sub.live_since || "").slice(0, 10)}
                    </p>
                    {sub.cancel_pending && (
                        <p className="text-[11px] text-amber-400" data-testid={`pro-plus-sub-cancel-pending-detail-${sub.id}`}>
                            Cancellation scheduled{sub.cancel_pending_at ? ` on ${sub.cancel_pending_at.slice(0, 10)}` : ""} — access continues
                            {sub.access_until ? ` until ${sub.access_until.slice(0, 10)}` : " until the current billing period ends"}, then this row auto-closes.
                        </p>
                    )}
                    {/* iter87.281 — Grant email state row (was "Clerk
                        invitation"). Only rendered for manual-grant
                        subs; other sources are Clerk-authed already. */}
                    {sub.source === "manual_grant" && (
                        <div
                            className="flex flex-wrap items-center gap-2 text-[11px] border border-neon/20 rounded p-2 bg-neon/[0.03]"
                            data-testid={`pro-plus-sub-invitation-${sub.id}`}
                        >
                            <Mail className="w-3 h-3 text-neon shrink-0" />
                            <span className="text-white/70">Grant email:</span>
                            <span
                                className="pill text-[9px] font-bold uppercase border-neon/40 text-neon"
                                data-testid={`pro-plus-sub-invitation-status-${sub.id}`}
                            >
                                {sub.grant_email_status || "not sent"}
                            </span>
                            {sub.grant_email_link_type && (
                                <span className="text-white/40 text-[10px]">
                                    · {sub.grant_email_link_type === "sign_in" ? "sign-in link" : "sign-up link"}
                                </span>
                            )}
                            {sub.grant_email_sent_at && (
                                <span className="text-white/40 text-[10px]">
                                    · {sub.grant_email_sent_at.slice(0, 16).replace("T", " ")}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => runAction(
                                    () => api.proPlusResendInvite(sub.id),
                                    "Grant email sent",
                                )}
                                disabled={busy}
                                data-testid={`pro-plus-sub-resend-invite-${sub.id}`}
                                className="brutal-btn-ghost text-[10px] py-1 ml-auto text-neon disabled:opacity-40"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                {sub.grant_email_status ? "Resend email" : "Send email"}
                            </button>
                        </div>
                    )}
                    {sub.audit_tail && sub.audit_tail.length > 0 && (
                        <div className="border border-[#222] p-2 rounded">
                            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Audit</p>
                            {sub.audit_tail.slice(-5).map((a, i) => (
                                <p key={i} className="text-[10px] text-zinc-400 font-mono">
                                    {(a.at || "").slice(0, 16).replace("T", " ")} · {a.action} · {a.actor}
                                    {a.reason ? ` (${a.reason})` : ""}
                                    {a.note ? ` — ${a.note}` : ""}
                                </p>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {sub.status === "live" && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setSuspendOpen((v) => !v)}
                                    data-testid={`pro-plus-sub-suspend-toggle-${sub.id}`}
                                    className="brutal-btn-ghost text-xs py-1.5 text-orange-400"
                                >
                                    <Pause className="w-3 h-3" /> Suspend…
                                </button>
                                <button
                                    type="button"
                                    onClick={() => runAction(
                                        () => api.proPlusClose(sub.id, null),
                                        "Closed",
                                    )}
                                    disabled={busy}
                                    data-testid={`pro-plus-sub-close-${sub.id}`}
                                    className="brutal-btn-ghost text-xs py-1.5"
                                >
                                    <XCircle className="w-3 h-3" /> Close
                                </button>
                            </>
                        )}
                        {sub.status === "suspended" && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => runAction(
                                        () => api.proPlusReinstate(sub.id),
                                        "Reinstated",
                                    )}
                                    disabled={busy}
                                    data-testid={`pro-plus-sub-reinstate-${sub.id}`}
                                    className="brutal-btn-primary text-xs py-1.5"
                                >
                                    <RotateCcw className="w-3 h-3" /> Reinstate
                                </button>
                                <button
                                    type="button"
                                    onClick={() => runAction(
                                        () => api.proPlusClose(sub.id, null),
                                        "Closed",
                                    )}
                                    disabled={busy}
                                    data-testid={`pro-plus-sub-close-${sub.id}`}
                                    className="brutal-btn-ghost text-xs py-1.5"
                                >
                                    <XCircle className="w-3 h-3" /> Close
                                </button>
                            </>
                        )}
                        {sub.status === "closed" && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => runAction(
                                        () => api.proPlusReopen(sub.id),
                                        "Reopened",
                                    )}
                                    disabled={busy}
                                    data-testid={`pro-plus-sub-reopen-${sub.id}`}
                                    className="brutal-btn-primary text-xs py-1.5"
                                    title="Undo close — restores Pro+ shell access and re-grants entitlement to the customer"
                                >
                                    <RotateCcw className="w-3 h-3" /> Reopen
                                </button>
                                <button
                                    type="button"
                                    onClick={() => runAction(
                                        () => api.proPlusArchive(sub.id),
                                        "Archived",
                                    )}
                                    disabled={busy}
                                    data-testid={`pro-plus-sub-archive-${sub.id}`}
                                    className="brutal-btn-ghost text-xs py-1.5"
                                >
                                    <Archive className="w-3 h-3" /> Archive
                                </button>
                            </>
                        )}
                        {sub.status === "archived" && (
                            <button
                                type="button"
                                onClick={() => setShowDelete((v) => !v)}
                                data-testid={`pro-plus-sub-delete-toggle-${sub.id}`}
                                className="brutal-btn-ghost text-xs py-1.5 text-red-400"
                            >
                                <Trash2 className="w-3 h-3" /> Delete…
                            </button>
                        )}
                    </div>
                    {suspendOpen && sub.status === "live" && (
                        <div className="flex gap-2 items-stretch flex-wrap" data-testid={`pro-plus-sub-suspend-row-${sub.id}`}>
                            <select
                                value={suspendReason}
                                onChange={(e) => setSuspendReason(e.target.value)}
                                data-testid={`pro-plus-sub-suspend-reason-${sub.id}`}
                                className="brutal-input text-xs"
                            >
                                <option value="past_due">Past due</option>
                                <option value="user_cancel_scheduled">User cancelled (scheduled)</option>
                                <option value="admin_suspend">Admin suspend</option>
                            </select>
                            <input
                                value={suspendNote}
                                onChange={(e) => setSuspendNote(e.target.value)}
                                placeholder="Optional note"
                                data-testid={`pro-plus-sub-suspend-note-${sub.id}`}
                                className="brutal-input text-xs flex-1"
                                maxLength={1000}
                            />
                            <button
                                type="button"
                                onClick={() => runAction(
                                    () => api.proPlusSuspend(sub.id, suspendReason, suspendNote || null),
                                    "Suspended",
                                ).then(() => { setSuspendOpen(false); setSuspendNote(""); })}
                                disabled={busy}
                                data-testid={`pro-plus-sub-suspend-submit-${sub.id}`}
                                className="brutal-btn-ghost text-xs py-2 text-orange-400"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirm suspend"}
                            </button>
                        </div>
                    )}
                    {showDelete && sub.status === "archived" && (
                        <div className="flex gap-2 items-stretch" data-testid={`pro-plus-sub-delete-row-${sub.id}`}>
                            <input
                                value={deleteConfirm}
                                onChange={(e) => setDeleteConfirm(e.target.value)}
                                placeholder={`Type "${sub.email}" to confirm delete`}
                                data-testid={`pro-plus-sub-delete-confirm-${sub.id}`}
                                className="brutal-input text-xs flex-1 font-mono"
                            />
                            <button
                                type="button"
                                onClick={() => runAction(
                                    () => api.proPlusDelete(sub.id, deleteConfirm.trim()),
                                    "Deleted",
                                )}
                                disabled={busy || deleteConfirm.trim().toLowerCase() !== sub.email}
                                data-testid={`pro-plus-sub-delete-submit-${sub.id}`}
                                className="brutal-btn-ghost text-xs py-2 text-red-400 disabled:opacity-40"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Delete"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
